'use client';

import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Edge, Handle, Node, Position } from 'reactflow';
import 'reactflow/dist/style.css';

import { Laptop, Monitor, Server, Smartphone } from 'lucide-react';

import {
  Cpu,
  Globe,
  HardDrive,
  KeyRound,
  Network,
  Shield,
  Wifi,
  WifiOff,
} from 'lucide-react';

import { DateFormatted, KVRow, SectionCard, Tag } from '@components';
import { useCompany } from '@hooks';
import { UsersService } from '@services/users/Users';

type NetbirdPeer = {
  id: string;
  name?: string;
  hostname?: string;
  dns_label?: string;
  ip?: string;
  ipv6?: string;
  connection_ip?: string;
  connected?: boolean;
  login_expired?: boolean;
  last_seen?: string;
  last_login?: string;
  created_at?: string;
  os?: string;
  kernel_version?: string;
  version?: string;
  ui_version?: string;
  ssh_enabled?: boolean;
  approval_required?: boolean;
  login_expiration_enabled?: boolean;
  ephemeral?: boolean;
  country_code?: string;
  city_name?: string;
  serial_number?: string;
  user_id?: string;
  accessible_peers_count?: number;
  groups?: {
    id: string;
    name: string;
    peers_count?: number;
    resources_count?: number;
    issued?: string;
  }[];
  extra_dns_labels?: string[];
  local_flags?: Record<string, boolean>;
};

type Props = {
  data: {
    peer?: NetbirdPeer;
  };
};

const userService = new UsersService();

const getPeerName = (peer: NetbirdPeer) =>
  peer.hostname || peer.name || peer.dns_label || peer.id;

const getInitials = (peer: NetbirdPeer) => {
  const name = getPeerName(peer);
  return name.slice(0, 2).toUpperCase();
};

const getPeerStatus = (peer: NetbirdPeer) => {
  if (peer.connected) {
    return {
      label: 'online',
      tagStyle: 'success',
      dot: 'bg-emerald-500',
      border: 'border-emerald-400',
      glow: 'shadow-emerald-500/20',
      edge: '#10b981',
    };
  }

  if (peer.login_expired) {
    return {
      label: 'expired',
      tagStyle: 'danger',
      dot: 'bg-orange-500',
      border: 'border-orange-400',
      glow: 'shadow-orange-500/20',
      edge: '#f97316',
    };
  }

  return {
    label: 'offline',
    tagStyle: 'neutral',
    dot: 'bg-slate-400',
    border: 'border-slate-300',
    glow: 'shadow-slate-400/10',
    edge: '#94a3b8',
  };
};

const BooleanValue = ({ value }: { value?: boolean }) => (
  <span
    className={`text-xs font-semibold ${
      value ? 'text-emerald-600' : 'text-slate-400'
    }`}
  >
    {value ? 'Enabled' : 'Disabled'}
  </span>
);

const getIconStyle = (peer: NetbirdPeer) => {
  const os = peer.os?.toLowerCase() ?? '';
  const name = peer.hostname?.toLowerCase() ?? '';

  if (
    os.includes('ubuntu') ||
    os.includes('debian') ||
    os.includes('linux') ||
    os.includes('server') ||
    name.includes('srv') ||
    name.includes('server')
  ) {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  }

  if (os.includes('windows')) {
    return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  }

  if (os.includes('darwin') || os.includes('mac')) {
    return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300';
  }

  if (
    os.includes('ios') ||
    os.includes('iphone') ||
    os.includes('ipad') ||
    os.includes('android')
  ) {
    return 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
  }

  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
};

const getPeerIcon = (peer: NetbirdPeer) => {
  const os = peer.os?.toLowerCase() ?? '';
  const name = peer.hostname?.toLowerCase() ?? '';

  if (
    os.includes('ubuntu') ||
    os.includes('debian') ||
    os.includes('linux') ||
    os.includes('server') ||
    name.includes('srv') ||
    name.includes('server')
  ) {
    return <Server className="w-4 h-4" />;
  }

  if (os.includes('windows')) {
    return <Monitor className="w-4 h-4" />;
  }

  if (os.includes('darwin') || os.includes('mac')) {
    return <Laptop className="w-4 h-4" />;
  }

  if (
    os.includes('ios') ||
    os.includes('iphone') ||
    os.includes('ipad') ||
    os.includes('android')
  ) {
    return <Smartphone className="w-4 h-4" />;
  }

  return <Monitor className="w-4 h-4" />;
};

const PeerNode = ({ data }: any) => {
  const peer: NetbirdPeer = data.peer;
  const isMain = data.isMain;

  const status = getPeerStatus(peer);
  const iconStyle = getIconStyle(peer);

  const statusDot =
    status.label === 'online'
      ? 'bg-emerald-500'
      : status.label === 'expired'
        ? 'bg-orange-500'
        : 'bg-slate-400';

  return (
    <div
      title={`${getPeerName(peer)} - ${peer.ip || '-'} - ${peer.os || '-'}`}
      className={`
        relative rounded-xl border bg-white dark:bg-slate-900
        border-slate-200 dark:border-slate-700
        shadow-sm transition

        ${
          isMain
            ? 'w-[220px] px-4 py-3 ring-2 ring-indigo-500/70 border-indigo-300 bg-indigo-50/50 dark:bg-indigo-950/30 shadow-md'
            : 'w-[180px] px-4 py-3'
        }
      `}
    >
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <Handle type="source" position={Position.Bottom} className="!opacity-0" />

      <div className="flex items-start gap-3 min-w-0">
        {/* ICON */}
        <div
          className={`
            flex items-center justify-center rounded-lg shrink-0
            ${isMain ? 'w-10 h-10' : 'w-9 h-9'}
            ${iconStyle}
          `}
        >
          {getPeerIcon(peer)}
        </div>

        {/* TEXT */}
        <div className="min-w-0 flex-1">
          <div
            className={`
              font-semibold text-slate-800 dark:text-slate-100 truncate
              ${isMain ? 'text-sm' : 'text-xs'}
            `}
          >
            {getPeerName(peer)}
          </div>

          <div className="text-xs text-slate-500 truncate mt-0.5">
            {peer.ip || '-'}
          </div>

          <div className="text-[10px] text-slate-400 truncate mt-0.5">
            {peer.os || 'Unknown OS'}
          </div>
        </div>

        {/* STATUS DOT */}
        <div
          className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${statusDot}`}
        />
      </div>

      {isMain && (
        <div className="mt-2 inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
          selected
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  peerNode: PeerNode,
};

const PeerTopologyFlow = ({
  peer,
  accessiblePeers,
  loading,
}: {
  peer: NetbirdPeer;
  accessiblePeers: NetbirdPeer[];
  loading: boolean;
}) => {
  const maxVisiblePeers = 18;
  const maxPerRow = 4;

  const visiblePeers = accessiblePeers.slice(0, maxVisiblePeers);
  const hiddenCount = Math.max(accessiblePeers.length - visiblePeers.length, 0);

  const rows = Math.max(1, Math.ceil(visiblePeers.length / maxPerRow));

  // deze moeten matchen met PeerNode widths
  const mainNodeWidth = 220;
  const nodeWidth = 180;

  const flowWidth = 920;
  const horizontalGap = 28;
  const verticalGap = 120;

  const mainY = 35;
  const peersStartY = 220;

  const flowHeight = Math.max(460, peersStartY + rows * verticalGap + 60);

  const { nodes, edges } = useMemo(() => {
    const mainNode: Node = {
      id: peer.id,
      type: 'peerNode',
      position: {
        x: flowWidth / 2 - mainNodeWidth / 2,
        y: mainY,
      },
      data: {
        peer,
        isMain: true,
      },
      draggable: false,
      selectable: false,
    };

    const peerNodes: Node[] = visiblePeers.map((accessiblePeer, index) => {
      const row = Math.floor(index / maxPerRow);
      const col = index % maxPerRow;

      const itemsInRow = Math.min(
        maxPerRow,
        visiblePeers.length - row * maxPerRow
      );

      const rowWidth =
        itemsInRow * nodeWidth + (itemsInRow - 1) * horizontalGap;

      const startX = flowWidth / 2 - rowWidth / 2;

      return {
        id: accessiblePeer.id,
        type: 'peerNode',
        position: {
          x: startX + col * (nodeWidth + horizontalGap),
          y: peersStartY + row * verticalGap,
        },
        data: {
          peer: accessiblePeer,
          isMain: false,
        },
        draggable: false,
        selectable: false,
      };
    });

    const peerEdges: Edge[] = visiblePeers.map((accessiblePeer) => {
      const peerStatus = getPeerStatus(accessiblePeer);

      return {
        id: `${peer.id}-${accessiblePeer.id}`,
        source: peer.id,
        target: accessiblePeer.id,
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: peerStatus.edge,
          strokeWidth: accessiblePeer.connected ? 1.8 : 1.4,
          opacity: accessiblePeer.connected ? 0.55 : 0.28,
        },
      };
    });

    return {
      nodes: [mainNode, ...peerNodes],
      edges: peerEdges,
    };
  }, [peer, visiblePeers]);

  return (
    <div
      className="relative rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/80 dark:bg-slate-900/40 overflow-hidden"
      style={{ height: flowHeight }}
    >
      {loading ? (
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-2 text-slate-400">
            <Network className="w-5 h-5 animate-pulse" />
            <span className="text-xs">Topology laden...</span>
          </div>
        </div>
      ) : accessiblePeers.length === 0 ? (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <Network className="w-6 h-6 mx-auto text-slate-300 mb-2" />

            <div className="text-sm font-medium text-slate-500">
              Geen accessible peers
            </div>

            <div className="text-xs text-slate-400">
              Deze peer heeft momenteel geen zichtbare relaties.
            </div>
          </div>
        </div>
      ) : (
        <>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.18 }}
            nodesConnectable={false}
            elementsSelectable={false}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={22} size={1} color="#e2e8f0" />
          </ReactFlow>

          <div className="absolute left-3 top-3 rounded-full bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 px-2.5 py-1 text-[10px] font-medium text-slate-500 shadow-sm">
            {accessiblePeers.length} accessible peer
            {accessiblePeers.length === 1 ? '' : 's'}
          </div>

          {hiddenCount > 0 && (
            <div className="absolute right-3 bottom-3 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-2.5 py-1 text-[10px] font-semibold shadow-sm">
              +{hiddenCount} hidden
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const VpnPeerContainer = ({ data }: Props) => {
  const peer = data?.peer;
  const { companyId } = useCompany();

  const [accessiblePeers, setAccessiblePeers] = useState<NetbirdPeer[]>([]);
  const [loadingAccessiblePeers, setLoadingAccessiblePeers] = useState(false);
  const [accessiblePeersError, setAccessiblePeersError] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!companyId || !peer?.id) return;

    let isMounted = true;

    const fetchAccessiblePeers = async () => {
      try {
        setLoadingAccessiblePeers(true);
        setAccessiblePeersError(null);

        const res = await userService.getAccessiblePeers(companyId, peer.id);

        if (!isMounted) return;
        const peers = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.peers)
              ? res.peers
              : Array.isArray(res?.data?.peers)
                ? res.data.peers
                : [];

        setAccessiblePeers(peers);
      } catch (error) {
        if (!isMounted) return;

        console.error('Failed to load accessible peers', error);
        setAccessiblePeers([]);
        setAccessiblePeersError('Accessible peers konden niet geladen worden.');
      } finally {
        if (isMounted) {
          setLoadingAccessiblePeers(false);
        }
      }
    };

    fetchAccessiblePeers();

    return () => {
      isMounted = false;
    };
  }, [companyId, peer?.id]);

  if (!peer) return null;

  const status = getPeerStatus(peer);
  const peerType = peer.user_id ? 'client device' : 'key registered peer';

  const reachableCount =
    accessiblePeers.length || peer.accessible_peers_count || 0;

  return (
    <div className="space-y-4">
      {/* HERO */}
      <div
        className="
          rounded-xl border bg-gradient-to-br from-white to-slate-50
          dark:from-slate-900 dark:to-slate-800/70
          border-slate-100 dark:border-slate-700/60 p-4 shadow-sm
        "
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div
                className={`
                  relative w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-md
                  ${status.dot}
                `}
              >
                {peer.connected && (
                  <span
                    className={`absolute inset-0 rounded-xl ${status.dot} opacity-30`}
                  />
                )}

                <span className="relative">{getInitials(peer)}</span>
              </div>

              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                  {getPeerName(peer)}
                </h2>

                <p className="text-xs text-slate-500 truncate">
                  {peer.dns_label || peer.id}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Tag label={status.label} style={status.tagStyle} lowercase />
              <Tag label={peerType} style="neutral" lowercase />

              {peer.approval_required && (
                <Tag label="approval required" style="pending" lowercase />
              )}

              {peer.ssh_enabled && (
                <Tag label="ssh enabled" style="info" lowercase />
              )}
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-2xl font-semibold text-slate-900 dark:text-white leading-none">
              {loadingAccessiblePeers ? '...' : reachableCount}
            </div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-1">
              reachable
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg bg-white/70 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
              <Globe className="w-3 h-3" />
              IP
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate mt-1">
              {peer.ip || '-'}
            </div>
          </div>

          <div className="rounded-lg bg-white/70 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
              <Cpu className="w-3 h-3" />
              OS
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate mt-1">
              {peer.os || '-'}
            </div>
          </div>

          <div className="rounded-lg bg-white/70 dark:bg-slate-800/80 border border-slate-100 dark:border-slate-700 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-slate-400">
              {peer.connected ? (
                <Wifi className="w-3 h-3" />
              ) : (
                <WifiOff className="w-3 h-3" />
              )}
              Seen
            </div>
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate mt-1">
              <DateFormatted date={peer.last_seen} noDate="-" />
            </div>
          </div>
        </div>
      </div>

      {/* TOPOLOGY */}
      <SectionCard title="Topology" icon={<Network className="w-4 h-4" />}>
        {accessiblePeersError && (
          <div className="mb-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700 dark:border-orange-900/50 dark:bg-orange-950/30 dark:text-orange-300">
            {accessiblePeersError}
          </div>
        )}

        <PeerTopologyFlow
          peer={peer}
          accessiblePeers={accessiblePeers}
          loading={loadingAccessiblePeers}
        />
      </SectionCard>

      {/* OVERVIEW */}
      <SectionCard title="Overview" icon={<Monitor className="w-4 h-4" />}>
        <KVRow label="Hostname" value={peer.hostname || peer.name || '-'} />
        <KVRow label="DNS label" value={peer.dns_label || '-'} />
        <KVRow label="Public IP" value={peer.connection_ip || '-'} />
        <KVRow
          label="Last seen"
          value={<DateFormatted date={peer.last_seen} noDate="-" />}
        />
        <KVRow
          label="Last login"
          value={<DateFormatted date={peer.last_login} noDate="-" />}
        />
      </SectionCard>

      {/* NETWORK */}
      <SectionCard title="Network" icon={<Globe className="w-4 h-4" />} colapse>
        <KVRow label="IPv4" value={peer.ip || '-'} />
        <KVRow label="IPv6" value={peer.ipv6 || '-'} />
        <KVRow
          label="Extra DNS"
          value={peer.extra_dns_labels?.join(', ') || '-'}
        />
        <KVRow
          label="Accessible peers"
          value={loadingAccessiblePeers ? 'Laden...' : reachableCount}
        />
      </SectionCard>

      {/* SYSTEM */}
      <SectionCard title="System" icon={<Server className="w-4 h-4" />} colapse>
        <KVRow label="OS" value={peer.os || '-'} />
        <KVRow label="Kernel" value={peer.kernel_version || '-'} />
        <KVRow label="Agent version" value={peer.version || '-'} />
        <KVRow label="UI version" value={peer.ui_version || '-'} />
        <KVRow label="Serial" value={peer.serial_number || '-'} />
      </SectionCard>

      {/* ACCESS */}
      <SectionCard
        title="Access & Security"
        icon={<Shield className="w-4 h-4" />}
        colapse
      >
        <KVRow label="SSH" value={<BooleanValue value={peer.ssh_enabled} />} />
        <KVRow
          label="Approval required"
          value={<BooleanValue value={peer.approval_required} />}
        />
        <KVRow
          label="Login expiration"
          value={<BooleanValue value={peer.login_expiration_enabled} />}
        />
        <KVRow
          label="Login expired"
          value={
            <span
              className={`text-xs font-semibold ${
                peer.login_expired ? 'text-orange-600' : 'text-slate-400'
              }`}
            >
              {peer.login_expired ? 'Yes' : 'No'}
            </span>
          }
        />
        <KVRow
          label="Ephemeral"
          value={<BooleanValue value={peer.ephemeral} />}
        />
      </SectionCard>

      {/* GROUPS */}
      <SectionCard
        title="Groups"
        icon={<KeyRound className="w-4 h-4" />}
        colapse
      >
        {peer.groups?.length ? (
          <div className="flex flex-wrap gap-2">
            {peer.groups.map((group) => (
              <Tag key={group.id} label={group.name} style="info" lowercase />
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500">No groups</div>
        )}
      </SectionCard>

      {/* LOCAL FLAGS */}
      {peer.local_flags && (
        <SectionCard
          title="Local Flags"
          icon={<HardDrive className="w-4 h-4" />}
          colapse
        >
          {Object.entries(peer.local_flags).map(([key, value]) => (
            <KVRow
              key={key}
              label={key.replaceAll('_', ' ')}
              value={<BooleanValue value={value} />}
            />
          ))}
        </SectionCard>
      )}
    </div>
  );
};

export default VpnPeerContainer;
